import { useState } from 'react';
import { X, Loader2, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNudgeInvestigationMutation } from '@/store/api/apiSlice';
import type { NudgeTarget } from '@/types';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface NudgeModalProps {
    isOpen: boolean;
    investigationId: string;
    employeeName: string;
    onClose: () => void;
    // Toast/banner surface for the result.
    onSent?: (message: string) => void;
}

const TARGETS: { value: NudgeTarget; label: string; hint: string }[] = [
    { value: 'both', label: 'Both', hint: 'Employee and employer' },
    { value: 'employee', label: 'Employee', hint: 'The person being checked' },
    { value: 'employer', label: 'Employer', hint: 'The requesting company' },
];

export default function NudgeModal({ isOpen, investigationId, employeeName, onClose, onSent }: NudgeModalProps) {
    const [nudge, { isLoading }] = useNudgeInvestigationMutation();
    const [target, setTarget] = useState<NudgeTarget>('both');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleClose = () => {
        setTarget('both');
        setMessage('');
        setError('');
        onClose();
    };

    const handleSend = async () => {
        setError('');
        try {
            const res = await nudge({
                id: investigationId,
                target,
                ...(message.trim() ? { message: message.trim() } : {}),
            }).unwrap();

            // Build a truthful toast from the `sent` booleans.
            const parts: string[] = [];
            if (res.sent.employee) parts.push('employee');
            if (res.sent.employer) parts.push('employer');
            const summary =
                parts.length > 0 ? `Reminder sent to ${parts.join(' and ')}.` : 'No reminder could be sent.';
            const warn =
                target === 'both' && !res.sent.employer && res.sent.employee
                    ? ' The employer had no email on file.'
                    : '';
            onSent?.(summary + warn);
            handleClose();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not send the reminder. Please try again.'));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50">
                            <BellRing className="h-5 w-5 text-[#FF6B35]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Send a reminder</h2>
                            <p className="text-xs text-gray-500">Nudge for {employeeName}'s investigation</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                    >
                        <X className="h-4 w-4 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5">
                    <label className="mb-2 block text-sm font-medium text-gray-700">Who to remind</label>
                    <div className="grid grid-cols-3 gap-2">
                        {TARGETS.map((t) => {
                            const active = target === t.value;
                            return (
                                <button
                                    key={t.value}
                                    type="button"
                                    onClick={() => setTarget(t.value)}
                                    className={`rounded-xl border px-3 py-2.5 text-center transition-colors ${
                                        active
                                            ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <span className="block text-sm font-medium text-gray-900">{t.label}</span>
                                    <span className="mt-0.5 block text-[10px] leading-tight text-gray-500">
                                        {t.hint}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <label className="mt-4 mb-1.5 block text-sm font-medium text-gray-700">Message (optional)</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="e.g. Kindly complete your consent form by Friday."
                        className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                    />

                    {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-5">
                    <Button
                        onClick={handleClose}
                        className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={isLoading}
                        className="flex items-center gap-2 rounded-lg bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#e0581f] disabled:opacity-50"
                    >
                        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Send reminder
                    </Button>
                </div>
            </div>
        </div>
    );
}
